import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LayoutContainerComponent } from '../../components/layout-container/layout-container.component';
import { SectionHeadingComponent } from '../../components/section-heading/section-heading.component';
import { CtaBannerComponent } from '../../components/cta-banner/cta-banner.component';

type LegalDocumentKey = 'privacyPolicy' | 'termsConditions' | 'consentCommunications';

interface LegalDocumentSection {
  id: number;
  heading: string;
  content?: string[];
  listType?: 'bullet' | 'number';
  listItems?: string[];
  footerText?: string;
  contactEmail?: string;
  subSections?: {
    subHeading: string;
    content?: string[];
    listType?: 'bullet' | 'number';
    listItems?: string[];
    footerText?: string;
  }[];
}

interface LegalDocument {
  documentId: string;
  title: string;
  companyName?: string;
  version?: string;
  lastUpdated?: string;
  sections: LegalDocumentSection[];
}

interface LegalCard {
  key: LegalDocumentKey;
  title: string;
  summary: string;
  configPath: string;
  configDocName: string;
}

@Component({
  selector: 'app-legal',
  standalone: true,
  imports: [
    LayoutContainerComponent,
    SectionHeadingComponent,
    CtaBannerComponent
  ],
  templateUrl: './legal.component.html',
  styleUrl: './legal.component.css'
})
export class LegalComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly legalCards: LegalCard[] = [
    {
      key: 'privacyPolicy',
      title: 'Privacy Policy',
      summary: 'How information is collected, used, stored, and protected.',
      configPath: environment.legalPage.sectionPrivacyPolicy.path,
      configDocName: environment.legalPage.sectionPrivacyPolicy.docName
    },
    {
      key: 'termsConditions',
      title: 'Terms & Conditions',
      summary: 'Service terms, acceptable use, responsibilities, and limitations.',
      configPath: environment.legalPage.sectionTermsConditions.path,
      configDocName: environment.legalPage.sectionTermsConditions.docName
    },
    {
      key: 'consentCommunications',
      title: 'Consent & Communications',
      summary: 'Consent language, communication notices, and message preferences.',
      configPath: environment.legalPage.sectionConsentCommunication.path,
      configDocName: environment.legalPage.sectionConsentCommunication.docName
    }
  ];

  documentsByKey: Partial<Record<LegalDocumentKey, LegalDocument | null>> = {};
  selectedDocumentKey: LegalDocumentKey = 'privacyPolicy';
  private privacyRequestResolved = false;

  ngOnInit(): void {
    this.selectedDocumentKey = 'privacyPolicy';
    this.privacyRequestResolved = false;
    this.changeDetectorRef.markForCheck();

    for (const card of this.legalCards) {
      this.http.get<LegalDocument>(this.buildDocumentUrl(card.configPath, card.configDocName), {
        transferCache: false
      }).pipe(catchError(() => of(null)))
        .subscribe((document) => {
          this.documentsByKey = {
            ...this.documentsByKey,
            [card.key]: document
          };

          if (card.key === 'privacyPolicy') {
            this.privacyRequestResolved = true;
          }

          this.resolveSelectedKey();
          this.changeDetectorRef.markForCheck();
        });
    }
  }

  onSelectDocument(key: LegalDocumentKey): void {
    this.selectedDocumentKey = key;
    this.changeDetectorRef.markForCheck();
  }

  get selectedDocument(): LegalDocument | null {
    return this.documentsByKey[this.selectedDocumentKey] ?? null;
  }

  get selectedDocumentTitle(): string {
    return this.legalCards.find((card) => card.key === this.selectedDocumentKey)?.title ?? 'Legal documents';
  }

  private buildDocumentUrl(path: string, fileName: string): string {
    const normalizedPrefix = environment.blobPrefix.endsWith('/')
      ? environment.blobPrefix
      : `${environment.blobPrefix}/`;
    const normalizedPath = path
      .replace(/\\+/g, '/')
      .replace(/^\/+/, '')
      .replace(/\/+$/, '');

    return `${normalizedPrefix}${normalizedPath}/${fileName}`;
  }

  private resolveSelectedKey(): void {
    const privacyDocument = this.documentsByKey.privacyPolicy;

    if (privacyDocument) {
      this.selectedDocumentKey = 'privacyPolicy';
      return;
    }

    if (!this.privacyRequestResolved) {
      return;
    }

    const firstAvailable = this.legalCards.find((card) => !!this.documentsByKey[card.key]);
    if (firstAvailable) {
      this.selectedDocumentKey = firstAvailable.key;
    }
  }
}
